import React, { useContext } from "react";
import axios, { AxiosError, AxiosInstance } from "axios";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { useParams, useHistory } from "react-router-dom";
import { boundarySections } from "@slootsantos/certain-forms/dist/forms/ce";
import { FormFieldType } from "@slootsantos/certain-forms/dist/types";

import Form from "forms/Form";
import { useClient } from "utils/client";
import { TrackingContext } from "context/Tracking";
import { AuthContext } from "context/Auth";
import { BasePage } from "components/BasePage/BasePage";
import { uploadSignedS3 } from "./Project/hooks/useCreateProject";
import { SpecialUploadType } from "components/Documents/types";
import { useProjectDetails } from "./Details/hooks/useProjectDetails";
import { ControlContext } from "context/Controls";
import { DocumentData, Project } from "types";
import {
  DraftIdentity,
  emptyDescription,
  selectBoundaryValues,
  validDescription,
} from "utils/formDrafts";

const descriptionDocument = (project?: Project) =>
  project?.documents?.find(
    (doc) => doc.type === SpecialUploadType.detailedDescription,
  );
export const descriptionKey = (projectId: string, doc?: DocumentData) => [
  "detailedDescription",
  projectId,
  doc?.id,
  doc?.version,
];

export const useBoundaryData = (projectId: string) => {
  const queryClient = useQueryClient();
  const client = useClient();
  const { t } = useTranslation();
  const { displayNotification } = useContext(ControlContext);
  const { project, fetchInProgress } = useProjectDetails(projectId);
  const document = descriptionDocument(project);
  const description = useQuery(
    descriptionKey(projectId, document),
    async () => {
      if (!document?.location) throw new Error("Missing description URL");
      let content: unknown;
      try {
        content = (await axios.get(document.location)).data;
      } catch (error) {
        if ((error as AxiosError).response?.status !== 403) throw error;
        // A signed URL can expire. Renew it once inside this request.
        const { data: refreshed } = await client.get<Project>(
          `/projects/${projectId}`,
        );
        queryClient.setQueryData(["projects", projectId], refreshed);
        const renewed = descriptionDocument(refreshed);
        if (!renewed?.location) throw error;
        content = (await axios.get(renewed.location)).data;
      }
      if (Array.isArray(content) && content.length === 0)
        return emptyDescription;
      if (!validDescription(content))
        throw new Error("Invalid description document");
      return content;
    },
    {
      enabled: !!project && !!document,
      retry: false,
      onError: (error) =>
        displayNotification?.(
          t("notification.saving.generic_failure"),
          (error as Error).message,
          "error",
        ),
    },
  );
  const ready =
    !!project && !fetchInProgress && (!document || description.isSuccess);
  return {
    project,
    ready,
    description: document ? description.data : emptyDescription,
  };
};

export const saveBoundaryValues = async (
  client: AxiosInstance,
  projectId: string,
  values: Record<string, any>,
) => {
  const selected = selectBoundaryValues(values);
  const { detailed_description, ...boundary } = selected.boundary;
  // PUT replaces the entire project. Read current data so a long-lived form
  // cannot restore old documents or overwrite unrelated project changes.
  const { data: latest } = await client.get<Project>(`/projects/${projectId}`);
  const project = structuredClone(latest);
  project.boundary = {
    ...project.boundary,
    ...boundary,
    // Partial rows are edits too. Do not acknowledge content we discarded.
    additional_boundaries: selected.additional_boundaries.map((row) => ({
      ...row,
    })),
  };
  delete (project.boundary as unknown as Record<string, unknown>)
    .detailed_description;
  await client.put(`/projects/${projectId}`, { payload: project });

  const uploadName = "/upload/detailed_description.slate.json";
  const file = new File(
    [JSON.stringify(detailed_description)],
    "detailed_description.slate.json",
    { type: "application/json" },
  );
  await uploadSignedS3(client, `${projectId}${uploadName}`, file);
  await client.post(`/projects/${projectId}/documents`, {
    type: SpecialUploadType.detailedDescription,
    name: uploadName,
  });
  return detailed_description;
};

const BoundaryPage = ({
  projectId,
  identity,
}: {
  projectId: string;
  identity?: DraftIdentity;
}) => {
  const { t } = useTranslation();
  const client = useClient();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { displayNotification } = useContext(ControlContext);
  const { track } = useContext(TrackingContext);
  const { project, ready, description } = useBoundaryData(projectId);
  const [initial, setInitial] = React.useState<{
    values: Record<string, any>;
    formData: { sections: typeof boundarySections };
  }>();

  React.useEffect(() => {
    if (initial || !ready || !project) return;
    const values = {
      ...structuredClone(project),
      boundary: {
        ...structuredClone(project.boundary),
        expected_misuse:
          project.boundary?.expected_misuse ||
          t("form.boundary.default_expected_misuse"),
        detailed_description: structuredClone(description),
      },
      additional_boundaries: structuredClone(
        project.boundary?.additional_boundaries || [],
      ),
    };
    const sections = structuredClone(boundarySections);
    if (
      !sections.some((s) =>
        s.fields.some((f) => f.name === "detailed_description"),
      )
    ) {
      sections.push({
        label: "form.boundaryDetailed.label",
        name: "boundary",
        subline: "form.boundaryDetailed.subline",
        info: "form.boundaryDetailed.info",
        fields: [
          {
            name: "detailed_description",
            label: "form.boundaryDetailed.description",
            type: FormFieldType.editor,
          },
        ],
      });
    }
    const additional = sections.find((s) => s.name === "additional_boundaries");
    if (additional) additional.count = values.additional_boundaries.length;
    setInitial({ values, formData: { sections } });
  }, [initial, ready, project, description, t]);

  const save = async (values: Record<string, any>) => {
    const content = await saveBoundaryValues(client, projectId, values);
    await queryClient.cancelQueries(["detailedDescription", projectId]);
    queryClient.setQueriesData(["detailedDescription", projectId], content);
    await queryClient.invalidateQueries(["projects", projectId]);
    track("update boundaries");
  };

  return (
    <BasePage
      loading={!initial}
      breadcrumbItems={[
        { label: "projects", location: "/dashboard" },
        {
          label: project?.projectdata?.product_name || projectId,
          location: `/projects/${projectId}`,
        },
        {
          label: "form.boundary.label",
          location: `/projects/${projectId}/edit/boundaries`,
        },
      ]}
    >
      {initial && (
        <Form
          persistStep
          formData={initial.formData}
          defaultValues={initial.values}
          persistence={{
            identity,
            onConflict: () =>
              displayNotification?.(
                t(
                  "form.boundary.draft_conflict_title",
                  "Local draft needs review",
                ),
                t(
                  "form.boundary.draft_conflict",
                  "Some older local edits conflict with saved changes. They remain in browser storage and were not applied.",
                ),
                "error",
              ),
          }}
          onAutosave={save}
          onSaveError={(error) =>
            displayNotification?.(
              t("notification.saving.generic_failure"),
              (error as Error).message,
              "error",
            )
          }
          onSubmit={async (values) => {
            await save(values);
            displayNotification?.(t("notification.saving.generic_success"), "");
            history.push(`/projects/${projectId}?saved=true`);
          }}
          saveText={t("form.boundary.save")}
          confirmationSubline="form.boundary.delete_confirmation_subline"
        />
      )}
    </BasePage>
  );
};

export const EditBoundaries = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { getUserInfo } = useContext(AuthContext);
  const { accountId, sub } = getUserInfo();
  const identity: DraftIdentity | undefined =
    accountId && sub
      ? { accountId, userId: sub, projectId, form: "boundary" }
      : undefined;
  return (
    <BoundaryPage
      key={`${accountId}:${sub}:${projectId}`}
      projectId={projectId}
      identity={identity}
    />
  );
};
